import React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { warningTriangleIcon } from "@progress/kendo-svg-icons";
import type { RawFileJobUsage } from "@domain/repositories/IRawFileRepository";

interface DeleteRawFileDialogProperties {
  fileName: string;
  folder: string;
  /**
  `.d` acquisitions are directory bundles of many objects rather than a single file.
  */
  isDirectoryBundle: boolean;
  /**
   * Folder targets are deleted recursively and are not usage-checked, so they always warn.
   */
  isFolder: boolean;
  /**
   * Jobs still referencing the file. Empty means no job depends on it, in which case no warning is shown.
   * `undefined` means the usage lookup has not resolved yet. Always empty for folders.
   */
  usedByJobs?: RawFileJobUsage[];
  isLoadingUsage: boolean;
  isDeleting: boolean;
  usageLookupFailed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const MAX_LISTED_JOBS = 10;

export const DeleteRawFileDialog: React.FC<DeleteRawFileDialogProperties> = ({
  fileName,
  folder,
  isDirectoryBundle,
  isFolder,
  usedByJobs,
  isLoadingUsage,
  isDeleting,
  usageLookupFailed,
  onConfirm,
  onCancel,
}) => {
  const jobCount = usedByJobs?.length ?? 0;
  const isInUse = jobCount > 0;
  const listedJobs = usedByJobs?.slice(0, MAX_LISTED_JOBS) ?? [];
  const hiddenJobCount = jobCount - listedJobs.length;

  let cascadeNotice = "";
  if (isFolder) {
    cascadeNotice = " Every file inside it will be removed.";
  } else if (isDirectoryBundle) {
    cascadeNotice = " Every file in this acquisition folder will be removed.";
  }

  let confirmLabel = "Delete";
  if (isDeleting) {
    confirmLabel = "Deleting…";
  } else if (isFolder || isInUse) {
    confirmLabel = "Delete anyway";
  }

  return (
    <Dialog title={isFolder ? "Delete folder" : "Delete file"} onClose={isDeleting ? undefined : onCancel} width={620}>
      <div style={{ padding: "10px" }}>
        <p style={{ marginTop: 0 }}>
          Permanently delete <strong>{fileName}</strong> from <strong>{folder || "Root"}</strong>?{cascadeNotice} This cannot be undone.
        </p>

        {isLoadingUsage && (
          <p role="status" style={{ fontSize: "0.8rem", color: "#666" }}>
            Checking whether any jobs use this file…
          </p>
        )}

        {/* Folder contents are not usage-checked, because that would mean a recursive scan of every file
            beneath the prefix. Warn unconditionally instead. */}
        {isFolder && (
          <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", color: "#b26a00" }}>
            <SvgIcon icon={warningTriangleIcon} />
            <span>Files in this folder may be used by existing jobs. Deleting it will break rerunning or reconfiguring those jobs.</span>
          </div>
        )}

        {/* Raw files have no referential integrity with jobs, so an in-use delete silently breaks reruns.
            The warning is only rendered when jobs actually reference the file. */}
        {!isFolder && !isLoadingUsage && isInUse && (
          <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", color: "#b26a00" }}>
            <SvgIcon icon={warningTriangleIcon} />
            <span>
              This file is used by {jobCount} {jobCount === 1 ? "job" : "jobs"}. Deleting it will break rerunning or reconfiguring {jobCount === 1 ? "that job" : "those jobs"}.
            </span>
          </div>
        )}

        {!isFolder && !isLoadingUsage && isInUse && (
          <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px", padding: "8px" }}>
            <ul aria-label="Jobs using this file" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {listedJobs.map((job) => (
                <li key={job.jobId} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5", fontSize: "0.85rem" }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={job.jobName}>
                    {job.jobName}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#666" }}>Created by {job.createdBy}</div>
                </li>
              ))}
            </ul>
            {hiddenJobCount > 0 && <p style={{ fontSize: "0.75rem", color: "#666", margin: "8px 0 0" }}>and {hiddenJobCount} more…</p>}
          </div>
        )}

        {!isFolder && !isLoadingUsage && usageLookupFailed && (
          <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#b26a00" }}>
            <SvgIcon icon={warningTriangleIcon} />
            <span>Could not check whether jobs use this file. It may still be referenced.</span>
          </div>
        )}
      </div>

      <DialogActionsBar>
        <Button onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button themeColor="error" onClick={onConfirm} disabled={isDeleting || isLoadingUsage}>
          {confirmLabel}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
